import {
  normalizeIdentityType,
  normalizePermissions,
  normalizeSubAccountStatus,
  PERMISSION_ORDER,
  getPermissionName,
} from "./permission";
import { normalizeDistributeIdentity } from "./workspace";

const buildAccountDisplay = (identity = {}) => {
  if (identity.account_display || identity.accountDisplay) {
    return identity.account_display || identity.accountDisplay;
  }

  if (identity.ent_qid) {
    return String(identity.ent_qid);
  }

  return "--";
};

export const adaptIdentityItem = (identity = {}) => {
  const identityType = normalizeIdentityType(identity.identity_type || identity.identityType);
  const permissionCodes = normalizePermissions(identity.permissions || identity.permissionCodes || []);

  return {
    identityId: Number(identity.identity_id || identity.identityId || 0),
    identityType,
    vendorId: identity.ent_qid || identity.vendorId || "",
    vendorName: identity.ent_name || identity.vendorName || "",
    qid: identity.qid || "",
    accountDisplay: buildAccountDisplay(identity),
    subAccountId: Number(identity.sub_account_id || identity.subAccountId || 0),
    status: normalizeSubAccountStatus(identity.status),
    permissionCodes,
    permissions: permissionCodes,
    distributeIdentity: normalizeDistributeIdentity(
      identity.distribute_identity ?? identity.distributeIdentity
    ),
    isCurrent: Boolean(identity.is_current || identity.isCurrent),
    lastLoginAt: identity.last_login_at || identity.lastLoginAt || "",
  };
};

export const adaptPermissionModules = (permissionModules = []) => {
  return PERMISSION_ORDER.map((code) => {
    const target = (Array.isArray(permissionModules) ? permissionModules : []).find((item) => item?.code === code) || {};
    return {
      code,
      name: target.name || getPermissionName(code),
      desc: target.desc || "",
      authorized: Boolean(target.authorized),
      disabledReason: target.disabled_reason || target.disabledReason || "",
    };
  });
};

export const adaptAccessInfo = (payload = {}) => {
  const identityList = Array.isArray(payload.identities) ? payload.identities.map(adaptIdentityItem) : [];
  const currentIdentityRaw = payload.current_identity || payload.currentIdentity || {};
  const hasCurrentIdentity = currentIdentityRaw && Object.keys(currentIdentityRaw).length > 0;
  const currentIdentity = hasCurrentIdentity ? adaptIdentityItem(currentIdentityRaw) : null;
  const permissionModules = adaptPermissionModules(payload.permission_modules || []);
  const authorizedFromModules = permissionModules.filter((item) => item.authorized).map((item) => item.code);
  const permissionCodes = normalizePermissions(
    payload.authorized_modules
      || (authorizedFromModules.length > 0 ? authorizedFromModules : null)
      || currentIdentity?.permissionCodes
      || []
  );
  const unauthorizedFromModules = permissionModules.filter((item) => !item.authorized).map((item) => item.code);
  const unauthorizedModules = normalizePermissions(
    payload.unauthorized_modules
      || (unauthorizedFromModules.length > 0 ? unauthorizedFromModules : null)
      || []
  );

  return {
    qid: payload.qid || "",
    userName: payload.user_name || payload.userName || "",
    nickName: payload.nick_name || payload.nickName || "",
    mobile: payload.mobile || "",
    email: payload.email || "",
    imageUrl: payload.image_url || payload.imageUrl || "",
    isUnionUser: Boolean(payload.is_union_user),
    identityCount: Number(payload.identity_count || identityList.length || 0),
    needSelectIdentity: Boolean(payload.need_select_identity),
    currentIdentity,
    identityList,
    identityType: currentIdentity?.identityType || "",
    vendorId: currentIdentity?.vendorId || "",
    vendorName: currentIdentity?.vendorName || "",
    permissionCodes,
    permissionModules,
    authorizedModules: permissionCodes,
    unauthorizedModules,
    hasAnyPermission: permissionCodes.length > 0,
  };
};

export const adaptSubAccount = (record = {}) => {
  const permissionTags = adaptPermissionModules(record.permission_tags || record.permissionModules || []);
  const permissions = normalizePermissions(
    record.permissions
      || permissionTags.filter((item) => item.authorized).map((item) => item.code)
      || []
  );

  return {
    id: Number(record.id || record.sub_account_id || 0),
    qid: record.qid || "",
    qidMasked: record.qid_masked || record.account_display || record.qid || "--",
    nickname: record.nickname || "",
    permissions,
    permissionTags,
    status: normalizeSubAccountStatus(record.status),
    createdAt: record.ctime || record.created_at || "--",
    lastLoginAt: record.last_login_at || "",
  };
};

export const adaptSubAccountList = (payload = {}) => {
  const list = Array.isArray(payload.list) ? payload.list.map(adaptSubAccount) : [];
  const subAccountQuota = Number(payload.quota || payload.limit || 20) || 20;
  const subAccountCurrentCount = Number(payload.used_quota || payload.currentCount || list.length || 0);
  const permissionModules = adaptPermissionModules(payload.permission_modules || []);

  return {
    list,
    total: Number(payload.total || payload.total_num || list.length || 0),
    activeTotal: Number(payload.active_total || 0),
    disabledTotal: Number(payload.disabled_total || 0),
    subAccountQuota,
    subAccountCurrentCount,
    subAccountQuotaReached: Boolean(payload.can_add === false) || subAccountCurrentCount >= subAccountQuota,
    canAdd: payload.can_add !== false,
    permissionModules,
  };
};
